import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FileText, Users, TrendingUp, AlertTriangle, Plus, Zap } from "lucide-react";
interface DashboardStats {
  totalListings: number;
  activeListings: number;
  newLeads7Days: number;
  newLeads30Days: number;
  topPerformingListing: any;
  listingsWithoutLeads: number;
  listingsWithMissingInfo: number;
}

export function BrokerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalListings: 0,
    activeListings: 0,
    newLeads7Days: 0,
    newLeads30Days: 0,
    topPerformingListing: null,
    listingsWithoutLeads: 0,
    listingsWithMissingInfo: 0,
  });
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string>('');

  useEffect(() => {
    if (user) {
      fetchUserRole();
      fetchDashboardStats();
    }
  }, [user]);

  const fetchUserRole = async () => {
    try {
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user?.id);
      
      if (roles && roles.length > 0) {
        const rolesList = roles.map(r => r.role);
        setUserRole(rolesList.includes('broker') ? 'broker' : 'seller');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      // Fetch listings
      const { data: listings, error: listingsError } = await supabase
        .from('listings')
        .select('*')
        .eq('broker_id', user?.id);

      if (listingsError) throw listingsError;

      // Fetch leads from last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const { data: leads30Days, error: leads30Error } = await supabase
        .from('leads')
        .select('*, listings!inner(*)')
        .eq('listings.broker_id', user?.id)
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (leads30Error) throw leads30Error;

      const { data: leads7Days, error: leads7Error } = await supabase
        .from('leads')
        .select('*, listings!inner(*)')
        .eq('listings.broker_id', user?.id)
        .gte('created_at', sevenDaysAgo.toISOString());

      if (leads7Error) throw leads7Error;

      // Calculate stats
      const activeListings = listings?.filter(l => l.status === 'live') || [];
      const listingsWithoutLeads = listings?.filter(l => {
        const listingLeads = leads30Days?.filter(lead => lead.listing_id === l.id) || [];
        return listingLeads.length === 0;
      }) || [];
      
      const listingsWithMissingInfo = listings?.filter(l => 
        !l.images || l.images.length === 0 || !l.description || !l.sector || !l.price
      ) || [];

      // Find top performing listing (by lead count)
      const listingLeadCounts = listings?.map(listing => ({
        ...listing,
        leadCount: leads30Days?.filter(lead => lead.listing_id === listing.id).length || 0
      })) || [];
      
      const topPerforming = listingLeadCounts.sort((a, b) => b.leadCount - a.leadCount)[0];

      setStats({
        totalListings: listings?.length || 0,
        activeListings: activeListings.length,
        newLeads7Days: leads7Days?.length || 0,
        newLeads30Days: leads30Days?.length || 0,
        topPerformingListing: topPerforming,
        listingsWithoutLeads: listingsWithoutLeads.length,
        listingsWithMissingInfo: listingsWithMissingInfo.length,
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="space-y-0 pb-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Welcome {userRole === 'broker' ? 'Broker' : 'Seller'}!</h1>
          <p className="text-sm md:text-base text-muted-foreground">Manage your listings, leads, and analytics</p>
        </div>
        <Button asChild className="w-fit">
          <Link to="/list-business">
            <Plus className="mr-2 h-4 w-4" />
            New Listing
          </Link>
        </Button>
      </div>

      {/* Key Stats */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Listings</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalListings}</div>
            <p className="text-xs text-muted-foreground">
              {stats.activeListings} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Leads (7 days)</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.newLeads7Days}</div>
            <p className="text-xs text-muted-foreground">
              {stats.newLeads30Days} in last 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Performing</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold truncate">
              {stats.topPerformingListing?.title || "No data"}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.topPerformingListing?.leadCount || 0} leads this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalListings > 0 ? ((stats.newLeads30Days / stats.totalListings) * 100).toFixed(1) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              Leads per listing
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      <div className="space-y-4">
        {stats.listingsWithoutLeads > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>{stats.listingsWithoutLeads}</strong> listings have no leads after 30 days. 
              Consider updating descriptions or boosting visibility.
            </AlertDescription>
          </Alert>
        )}

        {stats.listingsWithMissingInfo > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>{stats.listingsWithMissingInfo}</strong> listings are missing images or key information. 
              Complete profiles get 3x more inquiries.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common tasks to manage your listings and leads
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
          <Button variant="outline" className="justify-start" asChild>
            <Link to="/list-business">
              <Plus className="mr-2 h-4 w-4" />
              Create New Listing
            </Link>
          </Button>
          <Button variant="outline" className="justify-start">
            <Users className="mr-2 h-4 w-4" />
            Review New Leads
          </Button>
          <Button variant="outline" className="justify-start">
            <Zap className="mr-2 h-4 w-4" />
            Boost a Listing
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}